import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TemplateError, useApi } from '@tymely/api';
import { ITemplateVariable, IResponseTemplate, IOrganization, IPolicySet } from '@tymely/atoms';

import { TemplateUsageResponse } from '../../types/templateUsage';

export const RESPONSE_TEMPLATE_QUERY_KEY = 'response-template';
export const RENDER_TEMPLATE_QUERY_KEY = 'render-template';
export const FREE_VARS_QUERY_KEY = 'free-vars';
export const PROMPT_NAMES_QUERY_KEY = 'prompt-names';

const DEFAULT_RESPONSE_TEMPLATE: IResponseTemplate = {
    id: 0,
    response_txt: '',
    personalized: true,
    additional_data: {},
};

export const useFetchResponseQuery = (templateId?: number) => {
    const api = useApi();
    return useQuery<IResponseTemplate, TemplateError>({
        queryKey: [RESPONSE_TEMPLATE_QUERY_KEY, templateId],
        queryFn: () =>
            templateId ? api.get(`response-template/${templateId}`) : Promise.resolve(DEFAULT_RESPONSE_TEMPLATE),
        enabled: Boolean(templateId),
        initialData: DEFAULT_RESPONSE_TEMPLATE,
        meta: {
            errorMessage: `Failed to load template ${templateId}`,
        },
    });
};

export const useRenderTemplateQuery = (
    template: string,
    variables: Record<string, ITemplateVariable>,
    sanitize?: boolean,
    enabled = true,
    organizationId?: IOrganization['id'],
    intentId?: IPolicySet['id'],
) => {
    const api = useApi();
    return useQuery<string>({
        queryKey: [RENDER_TEMPLATE_QUERY_KEY, template, variables, sanitize, organizationId, intentId],
        queryFn: () =>
            api.post(
                'template/render',
                { template, variables, sanitize },
                {
                    params: {
                        organization_id: organizationId,
                        associated_intent_id: intentId,
                    },
                },
            ),
        enabled,
        placeholderData: (prevData) => prevData,
        retry: false,
    });
};

export const useTemplateFreeVariablesQuery = (
    template: string,
    organizationId?: IOrganization['id'],
    associatedIntentId?: IPolicySet['id'],
    options?: { enabled?: boolean },
) => {
    const api = useApi();
    const { enabled = true } = options ?? {};
    return useQuery<string[]>({
        queryKey: [FREE_VARS_QUERY_KEY, template, organizationId, associatedIntentId],
        queryFn: () =>
            api.post(
                'template/free-variables',
                { template },
                {
                    params: {
                        organization_id: organizationId,
                        associated_intent_id: associatedIntentId,
                    },
                },
            ),
        enabled,
        retry: false,
    });
};

export const EDIT_TEMPLATE_MUTATION_KEY = 'edit-response-template';
export const RESPONSE_TEMPLATE_MUTATION_KEY = 'response-template';

export const useEditResponseTemplateMutation = () => {
    const api = useApi();
    const queryClient = useQueryClient();
    return useMutation<
        IResponseTemplate,
        AxiosError | TemplateError,
        { template: IResponseTemplate; isPromptMode: boolean }
    >({
        mutationKey: [EDIT_TEMPLATE_MUTATION_KEY],
        mutationFn: ({ template, isPromptMode }) =>
            api.put(`response-template/${template.id}`, {
                ...template,
                additional_data: {
                    prompt_mode: isPromptMode,
                    prompt_template: isPromptMode
                        ? template.response_txt
                        : template.additional_data?.prompt_template || '',
                    standard_template: !isPromptMode
                        ? template.response_txt
                        : template.additional_data?.standard_template || '',
                },
            }),
        onSuccess(data) {
            queryClient.setQueryData([RESPONSE_TEMPLATE_MUTATION_KEY, data.id], () => data);
        },
    });
};

export const useCreateResponseTemplateMutation = () => {
    const api = useApi();
    const queryClient = useQueryClient();
    return useMutation<IResponseTemplate, AxiosError, Omit<IResponseTemplate, 'id'>>({
        mutationFn: (template) => api.post('response-template', template),
        onSuccess: (data) => {
            queryClient.setQueryData([RESPONSE_TEMPLATE_MUTATION_KEY, data.id], () => data);
        },
    });
};

export const PROCESS_TEMPLATE_MUTATION_KEY = 'process-template';

export const useProcessTemplateMutation = () => {
    const api = useApi();
    return useMutation<string, AxiosError, { response_txt: string; comment_id: number; template_type?: string }>({
        mutationKey: [PROCESS_TEMPLATE_MUTATION_KEY],
        mutationFn: (data) =>
            api.post('process_template_endpoint', {
                response_txt: data.response_txt,
                comment_id: data.comment_id,
                template_type: data.template_type || null,
            }),
    });
};

export const useGetPromptNamesQuery = () => {
    const api = useApi();
    return useQuery<string[]>({
        queryKey: [PROMPT_NAMES_QUERY_KEY],
        queryFn: () => api.get('prompt-templates/names'),
        retry: false,
    });
};

export const DUPLICATE_TEMPLATE_MUTATION_KEY = 'duplicate-response-template';

export const useDuplicateResponseTemplateMutation = () => {
    const api = useApi();
    const queryClient = useQueryClient();
    return useMutation<number, AxiosError, number>({
        mutationKey: [DUPLICATE_TEMPLATE_MUTATION_KEY],
        mutationFn: (templateId) =>
            api.post('duplicate_response_template', null, {
                params: { template_id: templateId },
            }),
        onSuccess: (newTemplateId) => {
            queryClient.invalidateQueries({ queryKey: [RESPONSE_TEMPLATE_QUERY_KEY, newTemplateId] });
        },
    });
};

export const TEMPLATE_USAGE_QUERY_KEY = 'template-usage';

export const useGetTemplateUsage = (templateId?: number) => {
    const api = useApi();
    return useQuery<TemplateUsageResponse, AxiosError>({
        queryKey: [TEMPLATE_USAGE_QUERY_KEY, templateId],
        queryFn: () => api.get(`policy/${templateId}/usage`),
        enabled: Boolean(templateId),
        meta: {
            errorMessage: `Failed to load template usage for ${templateId}`,
        },
    });
};
